import { isAxiosError } from 'axios';

import { QueryClient } from '@tanstack/react-query';

import { DEFAULT_NON_RETRY_STATUS_CODES } from '../constants/status';

import type { Query, StaleTimeFunction } from '@tanstack/react-query';
import type { StatusCode } from '../constants';

export const getHttpStatus = (error: unknown): StatusCode | null => {
  if (isAxiosError(error)) {
    return (error?.response?.status as StatusCode) ?? null;
  }
  // axios 인터셉터(onRejected)가 에러를 spread({ ...error })로 재구성하면 isAxiosError가 검사하는
  // prototype 플래그(isAxiosError)를 잃어 isAxiosError(error)가 false가 된다. 그러나 response는
  // enumerable own 속성이라 보존되므로, 이 경우에도 response.status로 http status를 추출한다.
  // (401 제외·403 화면 분기·non-retry/non-refetch 판별이 spread 에러에서도 동작하게 하기 위함)
  if (error && typeof error === 'object' && 'response' in error) {
    const status = (error as { response?: { status?: unknown } }).response?.status;
    return typeof status === 'number' ? (status as StatusCode) : null;
  }
  return null;
};

export const getQueryRefetchOnWindowFocus = <
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(
  query: Query<TQueryFnData, TError, TData, TQueryKey>,
  nonRefetchStatusCodes: StatusCode[] = DEFAULT_NON_RETRY_STATUS_CODES,
) => {
  const status = getHttpStatus(query.state?.error);
  if (status && nonRefetchStatusCodes.includes(status)) return false;
  return true;
};

export const getQueryRetry = (
  failureCount: number,
  error: unknown,
  nonRetryStatusCodes: StatusCode[] = DEFAULT_NON_RETRY_STATUS_CODES,
  defaultCount = 3,
) => {
  const status = getHttpStatus(error);
  if (status && nonRetryStatusCodes.includes(status)) return false;
  return failureCount < defaultCount;
};

/**
 * 전역 QueryClient 기본값이 적용된 인스턴스를 생성한다.
 *
 * @description
 * staleTime (15초):
 * - 빠른 페이지 이동(목록↔상세) 시 불필요한 refetch를 방지한다.
 * - invalidateQueries()는 staleTime을 override하므로 뮤테이션 후 갱신에는 영향이 없다.
 * - usePollingQuery의 refetchInterval과 독립적으로 동작한다.
 *
 * gcTime (5분, React Query 기본값):
 * - 비활성 쿼리 캐시를 5분간 유지하여 페이지 복귀 시 즉시 캐시 데이터를 표시한다.
 * - 서비스별 독립 QueryClient이므로 크로스 모듈 메모리 누적 이슈 없음.
 *
 * refetchOnWindowFocus:
 * - 401 Unauthorized, 403 Forbidden, 404 Not Found 에러 발생 시 refetch를 수행하지 않는다.
 *
 * retry:
 * - 401 Unauthorized, 403 Forbidden, 404 Not Found 에러는 재시도하지 않는다.
 * - 그 외 네트워크 에러는 최대 3회까지 재시도한다.
 *
 * 개별 queryOptions 팩토리(예: VPC)에서 명시한 값은 이 전역 기본값을 override한다.
 *
 * @see https://tanstack.com/query/v5/docs/framework/react/guides/important-defaults
 * @see https://tkdodo.eu/blog/practical-react-query
 */

type OverrideQueriesConfig = {
  gcTime?: number;
  staleTime?: StaleTimeFunction<unknown, Error, unknown, readonly unknown[]>;
};

export const createQueryClient = (overrideQueriesConfig?: OverrideQueriesConfig) =>
  new QueryClient({
    defaultOptions: {
      hydrate: {
        queries: {
          gcTime: Infinity,
        },
      },
      queries: {
        networkMode: 'always',
        staleTime: 15 * 1000, // 15초
        gcTime: 5 * 60 * 1000, // 5분
        refetchOnWindowFocus: query => getQueryRefetchOnWindowFocus(query),
        retry: (failureCount, error) => getQueryRetry(failureCount, error),
        ...overrideQueriesConfig,
      },
      mutations: {
        networkMode: 'always',
      },
    },
  });
